/**
 * Modal queue that enforces one modal showing at a time, but allows all modals
 * an opportunity to display.
 *
 * Modals are enqueued with a priority assigned. Modals with a higher priority
 * will display before those with a lower priority. If a modal is currently
 * visible and a higher priority modal is enqueued, the lower priority modal
 * will be hidden. Once the higher priority modal is closed, the lower priority
 * modal will re-display.
 *
 * See `useQueue` usage instructions.
 */

type Modal = {
  id: number;
  priority: number;
};

type SetCurrent = (_: number) => void;

type EnqueueOptions = {
  priority: number;
};

type DequeueOptions = {
  goBackInHistory?: boolean;
};

type EventName = 'enqueued' | 'dequeued';

export type EventPayload<T extends EventName> = T extends 'enqueued'
  ? { id: number } & EnqueueOptions
  : T extends 'dequeued'
  ? { id: number } & DequeueOptions
  : never;
export type EventListener<T extends EventName> = (
  payload: EventPayload<T>
) => void;
type EventListeners = {
  [K in EventName]: EventListener<K>[];
};

const defaultDequeueOptions: DequeueOptions = {
  goBackInHistory: true
};

export default class ModalQueue {
  private modals: Modal[] = [];

  private readonly setCurrent;

  private listeners: EventListeners = {
    enqueued: [],
    dequeued: []
  };

  constructor(setCurrent: SetCurrent) {
    this.setCurrent = () => {
      setCurrent(this.modals[0]?.id ?? -1);
    };
  }

  /**
   * Enqueue a modal by its ID and priority. If the modal is already in the
   * queue, it will not be added again.
   */
  enqueue(id: number, priority: number) {
    if (this.has(id)) return;

    const modalsCount = this.modals.length;
    const modal: Modal = { id, priority };
    this.modals = [...this.modals, modal].sort(
      (a, b) => b.priority - a.priority
    );

    if (modalsCount < this.modals.length) {
      this.setCurrent();
      this.emit('enqueued', { id, priority });
    }
  }

  /**
   * Dequeue a modal by its ID.
   */
  dequeue(id: number, opts?: DequeueOptions) {
    const options = { ...defaultDequeueOptions, ...opts };

    const modalsCount = this.modals.length;
    this.modals = this.modals.filter(m => m.id !== id);

    if (modalsCount > this.modals.length) {
      this.setCurrent();
      this.emit('dequeued', { id, ...options });
    }
  }

  /**
   * Check if a modal is in the queue by its ID.
   */
  has(id: number) {
    return this.modals.some(m => m.id === id);
  }

  /**
   * Register an event listener for when modals are enqueued or dequeued.
   */
  on<T extends EventName>(eventName: T, listener: EventListener<T>) {
    this.listeners[eventName].push(listener);
  }

  /**
   * Unregister an event listener.
   */
  off<T extends EventName>(eventName: T, listener: EventListener<T>) {
    // @ts-ignore
    this.listeners[eventName] = this.listeners[eventName].filter(
      fn => fn !== listener
    );
  }

  private emit<T extends EventName>(eventName: T, payload: EventPayload<T>) {
    this.listeners[eventName].forEach(listener => listener(payload));
  }
}
